import React from 'react';
import { gql } from '@soundxyz/gql-string';
import { MINT_COMPONENT_HEADING_SUBTEXT } from '@components/Song/MintComponents/shared/constants';
import { EditionCover } from '@components/basics/EditionCover';
import { Button } from '@components/main-components/button/Button';
import { StandardModal } from '@components/main-components/modals/StandardModal';
import { useAuth } from '@context/auth/base';
import { ModalContext, ModalType } from '@context/modal';
import { useQuery, YouOwnModalDocument } from '@graphql/reactQuery';
import { SPACE } from '@utils/constants';
import { getSingularOrPlural } from '@utils/helpers';
import { CommentModalV2 } from './CommentV2';

/*#__PURE__*/ gql(/* GraphQL */ `
  query YouOwnModal($releaseId: UUID!, $publicAddress: Address) {
    userByAddress(publicAddress: $publicAddress) {
      id
      collectedReleases(filter: { releaseIds: [$releaseId] }) {
        edges {
          node {
            id
            vipNftsCount
            gaNftsCount
            goldenEgg {
              id
            }
            release {
              id
              auctionContractType
              title
              coverImage {
                id
                url
              }
              staticCoverImage {
                id
                url
              }
              gaCoverImage {
                id
                url
              }
              staticGaCoverImage {
                id
                url
              }
              vipCoverImage {
                id
                url
              }
              staticVipCoverImage {
                id
                url
              }
              goldenEggImage {
                id
                url
              }
              animatedGoldenEggImageOptimized {
                id
                url
              }
            }
          }
        }
      }
    }
  }
`);

export const YouOwnModal = ({
  releaseId,
  editionsOwned,
}: {
  releaseId: string;
  editionsOwned: number;
}) => {
  const { authUser, isAuthenticatedWithAPI } = useAuth();
  const { closeCancelModal, openModal } = ModalContext.useContainer();

  const publicAddr = authUser?.publicAddress;

  const {
    data: youOwnData,
    isLoading,
    error,
  } = useQuery(YouOwnModalDocument, {
    variables: {
      releaseId,
      publicAddress: publicAddr,
    },
    staleTime: 0,
    enabled: isAuthenticatedWithAPI,
    filterQueryKey: [publicAddr, releaseId],
  });

  const collectedReleases = youOwnData?.data.userByAddress?.collectedReleases.edges.map(
    edge => edge.node,
  );

  if (!collectedReleases || !collectedReleases.length) {
    return;
  }

  const releaseCollectedInfo = collectedReleases[0];

  if (!releaseCollectedInfo) {
    return;
  }

  const { release, gaNftsCount, vipNftsCount, goldenEgg } = releaseCollectedInfo;
  const hasGoldenEgg = !!goldenEgg;
  const releaseHasGa = !!release.gaCoverImage;

  const fallBackImage = release.staticCoverImage?.url || release.coverImage?.url;
  const gaImage = release.staticGaCoverImage?.url || release.gaCoverImage?.url || fallBackImage;
  const vipCoverImage =
    release.staticVipCoverImage?.url || release.vipCoverImage?.url || fallBackImage;
  const goldenEggImage =
    release.animatedGoldenEggImageOptimized?.url || release.goldenEggImage.url || fallBackImage;

  const isTieredEdition = release.auctionContractType === 'TIERED_EDITION' && !!releaseHasGa;

  const rows = (
    <>
      {isTieredEdition ? (
        <>
          {gaNftsCount > 0 && (
            <EditionOwnedRow
              title={release.title}
              releaseCover={gaImage}
              heading={MINT_COMPONENT_HEADING_SUBTEXT.FREE.heading}
              editionCount={gaNftsCount}
            />
          )}
          {vipNftsCount > 0 && (
            <EditionOwnedRow
              title={release.title}
              releaseCover={vipCoverImage}
              heading="Limited Edition"
              editionCount={vipNftsCount}
            />
          )}
        </>
      ) : (
        <EditionOwnedRow
          title={release.title}
          releaseCover={fallBackImage}
          heading="Limited Edition"
          editionCount={hasGoldenEgg ? editionsOwned - 1 : editionsOwned}
        />
      )}

      {hasGoldenEgg && (
        <EditionOwnedRow
          title={release.title}
          releaseCover={goldenEggImage}
          heading="Golden Egg"
          editionCount={1}
        />
      )}
    </>
  );
  const body = (
    <div className="flex flex-col gap-4 pb-1">
      {isLoading || error ? (
        <>
          <RowSkeleton />
          <RowSkeleton />
        </>
      ) : (
        rows
      )}

      <Button
        variant="primary"
        label="Comment"
        fullWidth
        size="L"
        onClick={() => {
          openModal(ModalType.COMMENT_MODAL_V2, {
            body: <CommentModalV2 releaseId={releaseId} />,
          });
        }}
      />
    </div>
  );
  return (
    <StandardModal
      body={body}
      closeModal={closeCancelModal}
      title={`${!!editionsOwned ? `${editionsOwned} ` : ''}${getSingularOrPlural({
        word: 'edition',
        count: editionsOwned,
      })} collected`}
      bodyWithoutPaddingBottom
    />
  );
};

const EditionOwnedRow = ({
  title,
  releaseCover,
  heading,
  editionCount,
}: {
  title: string;
  releaseCover: string;
  heading: string;
  editionCount: number;
}) => {
  return (
    <div className="flex h-14 w-full items-center gap-2">
      <EditionCover
        releaseTitle={title}
        releaseCover={releaseCover}
        imageSize={48}
        className="!h-14 w-12 min-w-[48px] rounded-md"
        imageClassName="rounded-md"
      />
      <div className="flex w-full flex-col items-start">
        <h3 className="font-title text-title-xxs font-medium text-black">{heading}</h3>
        <p className="font-base text-base-s text-base700">
          {editionCount}
          {SPACE}
          {getSingularOrPlural({
            word: 'edition',
            count: editionCount,
          })}
        </p>
      </div>
    </div>
  );
};

const RowSkeleton = () => (
  <div className="flex h-14 w-full items-center gap-2">
    <div className="aspect-square h-12 w-12 animate-pulse rounded-md bg-neutral300" />
    <div className="flex w-full flex-col items-start gap-0.5">
      <div className="h-4 w-32 animate-pulse rounded-sm bg-neutral300" />
      <div className="h-3.5 w-20 animate-pulse rounded-sm bg-neutral300" />
    </div>
  </div>
);
